/* Shared lifecycle: one heartbeat, no requests, storage, tracking or user input. */
(() => {
  'use strict';
  if (customElements.get('moonmeet-events')) return;
  const active = new Set();
  let timer, adapter, sky, skyAt, lastZone, moon, moonAt, events, eventsAt, timelineState, timelineAt, refreshing = false;
  const getAdapter = () => adapter ||= window.MoonmeetAstronomy.create(window.Astronomy);
  const readTimeline = (now, zone, force = false) => {
    zone = window.MoonmeetEventTimeline.zoneOf(zone);
    const day = window.MoonmeetEventTimeline.dateKey(now, zone);
    if (force || !timelineState || timelineState.today !== day || timelineState.zone !== zone || now < timelineAt || now - timelineAt > 60000) {
      timelineState = window.MoonmeetEventTimeline.timeline(now, zone, getAdapter());
      timelineAt = now;
    }
    return timelineState;
  };
  const recordFor = (event) => {
    if (!event) return null;
    return [...document.querySelectorAll('[data-event-record]')].find((item) =>
      item.dataset.phase === event.phase && Math.abs(Number(item.dataset.epoch) - event.time) <= 120000) || null;
  };
  const setLink = (node, event) => {
    if (!node) return;
    const url = recordFor(event)?.dataset.url;
    if (url?.startsWith('/') && !url.startsWith('//')) { node.href = url; node.hidden = false; }
    else { node.removeAttribute('href'); node.hidden = true; }
  };
  const readMoon = (now, force = false) => {
    if ((force && !refreshing) || !moon || now < moonAt || Math.floor(now / 60000) !== Math.floor(moonAt / 60000)) {
      moon = undefined;
      moon = getAdapter().moon(now); moonAt = now;
    }
    return moon;
  };
  const localTime = (time) => {
    const locale = document.documentElement.lang || undefined;
    const formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeStyle: 'short' });
    const zone = formatter.resolvedOptions().timeZone;
    return { label: `${formatter.format(time)} · ${zone}`, zone };
  };
  const setText = (node, text) => { if (node && node.textContent !== text) node.textContent = text; };
  const heartbeat = (force = false) => {
    if (document.hidden) return;
    if (force) moon = undefined;
    refreshing = true;
    try { for (const node of active) node.refresh(force); }
    finally { refreshing = false; }
  };
  const register = (node) => {
    active.add(node);
    if (!timer) timer = setInterval(heartbeat, 1000);
    node.refresh(true);
  };
  const unregister = (node) => {
    active.delete(node);
    if (!active.size) { clearInterval(timer); timer = undefined; }
  };
  document.addEventListener('visibilitychange', () => { if (!document.hidden) heartbeat(true); });
  window.addEventListener('pageshow', () => heartbeat(true));

  class MoonmeetEvents extends HTMLElement {
    connectedCallback() { register(this); }
    disconnectedCallback() { unregister(this); }
    refresh(force) {
      try {
        const now = Date.now();
        const zone = window.MoonmeetEventTimeline.zoneOf(this.dataset.zone || 'America/Indianapolis');
        const state = readTimeline(now, zone, force);
        const primary = state.current || state.next;
        if (this.eventUtc !== primary.utc || this.zone !== zone || this.day !== state.today || force) {
          const date = localTime(primary.time);
          const hero = this.closest('.mm-hero');
          setText(hero?.querySelector('[data-hero-heading]'), state.current ? hero.dataset.todayHeading : hero.dataset.nextHeading);
          hero?.querySelector('[data-hero-intro-next]')?.toggleAttribute('hidden', !!state.current);
          hero?.querySelector('[data-hero-intro-today]')?.toggleAttribute('hidden', !state.current);
          setText(this.querySelector('[data-phase-name]'), this.dataset[primary.phase]);
          const time = this.querySelector('[data-event-time]');
          time.dateTime = primary.utc;
          setText(time, date.label);
          setText(this.querySelector('[data-celebration-date]'), `${this.dataset.celebrationDate} ${primary.celebrationDate} · ${zone}`);
          this.querySelector('[data-celebrating]')?.toggleAttribute('hidden', !state.current);
          this.querySelector('[data-next-label]')?.toggleAttribute('hidden', !!state.current);
          this.querySelector('[data-secondary-label]')?.toggleAttribute('hidden', !state.current);
          setLink(hero?.querySelector('[data-primary-event-link]'), primary);
          for (const [key, event] of [['previous', state.previous], ['current', state.current], ['next', state.next]]) {
            const item = hero?.querySelector(`[data-timeline-${key}]`);
            if (!item) continue;
            item.hidden = !event;
            if (event) {
              setText(item.querySelector('[data-timeline-phase]'), this.dataset[event.phase]);
              setLink(item.querySelector('a'), event);
            }
          }
          this.eventUtc = primary.utc; this.zone = zone; this.day = state.today;
          setText(this.querySelector('[data-event-announcement]'), `${state.current ? this.dataset.celebrating : this.dataset.nextLabel} ${this.dataset[primary.phase]} · ${date.label}`);
        }
        const remaining = window.MoonmeetAstronomy.remaining(state.next.time, now);
        for (const key of ['days', 'hours', 'minutes', 'seconds']) setText(this.querySelector(`[data-count="${key}"]`), String(remaining[key]).padStart(2, '0'));
        this.querySelector('[data-countdown]').hidden = false;
        this.querySelector('[data-event-details]').hidden = false;
        this.querySelector('[data-event-fallback]').hidden = true;
        this.dataset.state = 'ready';
        this.updateSession({ ...state, now });
      } catch {
        this.querySelector('[data-countdown]').hidden = true;
        this.querySelector('[data-event-details]').hidden = true;
        this.querySelector('[data-event-fallback]').hidden = false;
        this.querySelector('[data-community-session]')?.setAttribute('hidden', '');
        setText(this.querySelector('[data-event-announcement]'), this.dataset.failure);
        this.dataset.state = 'fallback';
        this.eventUtc = undefined;
        timelineState = undefined;
      }
    }
    updateSession(state) {
      const session = this.querySelector('[data-community-session]');
      if (!session) return;
      const strictUtc = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
      const time = Date.parse(session.dataset.utc);
      const target = Date.parse(session.dataset.phaseUtc);
      const valid = strictUtc.test(session.dataset.utc) && strictUtc.test(session.dataset.phaseUtc)
        && Number.isFinite(time) && Number.isFinite(target)
        && new Date(time).toISOString() === session.dataset.utc.replace('Z', '.000Z')
        && new Date(target).toISOString() === session.dataset.phaseUtc.replace('Z', '.000Z');
      // Associate an explicitly entered event with the calculated phase to the minute.
      if (!valid || !Number.isFinite(time) || time <= state.now || ![state.current, state.next].some((event) => event && Math.abs(target - event.time) <= 60000)) { session.hidden = true; return; }
      const label = session.querySelector('time');
      label.dateTime = new Date(time).toISOString();
      setText(label, localTime(time).label);
      session.hidden = false;
    }
  }
  class MoonmeetSky extends HTMLElement {
    connectedCallback() { register(this); }
    disconnectedCallback() { unregister(this); }
    refresh(force) {
      try {
        const now = Date.now();
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (force || !sky || now < skyAt || now - skyAt > 60000 || zone !== lastZone) {
          sky = readMoon(now, force); skyAt = now; lastZone = zone;
        }
        const phases = JSON.parse(this.dataset.phases);
        setText(this.querySelector('[data-sky-phase]'), phases[sky.phaseIndex]);
        setText(this.querySelector('[data-sky-light]'), `${new Intl.NumberFormat(document.documentElement.lang, { maximumFractionDigits: 0, style: 'percent' }).format(sky.fraction)} ${this.dataset.illuminated}`);
        const time = this.querySelector('time');
        time.dateTime = new Date(sky.time).toISOString();
        setText(time, localTime(sky.time).label);
        this.querySelector('[data-sky-values]').hidden = false;
        this.querySelector('[data-sky-fallback]').hidden = true;
      } catch {
        sky = undefined;
        skyAt = undefined;
        this.querySelector('[data-sky-values]').hidden = true;
        this.querySelector('[data-sky-fallback]').hidden = false;
      }
    }
  }
  class MoonmeetDashboard extends HTMLElement {
    connectedCallback() { register(this); }
    disconnectedCallback() { unregister(this); }
    refresh(force) {
      try {
        const now = Date.now();
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        moon = readMoon(now, force);
        if (this.observed === moon.time && this.zone === zone && !force) return;
        const phase = JSON.parse(this.dataset.phases)[moon.phaseIndex];
        const light = new Intl.NumberFormat(document.documentElement.lang, { style: 'percent', maximumFractionDigits: 1 }).format(moon.fraction);
        setText(this.querySelector('[data-moon-phase]'), phase);
        setText(this.querySelector('[data-moon-light]'), light);
        setText(this.querySelector('[data-moon-sign]'), JSON.parse(this.dataset.signs)[moon.signIndex]);
        setText(this.querySelector('[data-moon-direction]'), moon.waxing ? this.dataset.waxing : this.dataset.waning);
        const disk = window.MoonmeetAstronomy.phaseDisk(moon.fraction, moon.waxing);
        this.querySelector('[data-moon-lit]').setAttribute('d', disk.path);
        this.querySelector('[data-moon-lit]').setAttribute('transform', disk.transform);
        this.querySelector('[data-moon-disk]').setAttribute('aria-label', `${phase} · ${light}`);
        const time = this.querySelector('[data-moon-observed]');
        time.dateTime = new Date(moon.time).toISOString();
        setText(time, localTime(moon.time).label);
        setText(this.querySelector('[data-moon-announcement]'), `${phase} · ${JSON.parse(this.dataset.signs)[moon.signIndex]}`);
        this.querySelector('[data-moon-values]').hidden = false;
        this.querySelector('[data-moon-fallback]').hidden = true;
        this.dataset.state = 'ready'; this.observed = moon.time; this.zone = zone;
      } catch {
        moon = undefined; moonAt = undefined; this.observed = undefined;
        this.querySelector('[data-moon-values]').hidden = true;
        this.querySelector('[data-moon-fallback]').hidden = false;
        setText(this.querySelector('[data-moon-announcement]'), this.dataset.failure);
        this.dataset.state = 'fallback';
      }
    }
  }
  class MoonmeetUpcoming extends HTMLElement {
    connectedCallback() { register(this); }
    disconnectedCallback() { unregister(this); }
    refresh(force) {
      try {
        const now = Date.now();
        const zone = window.MoonmeetEventTimeline.zoneOf(this.dataset.zone || 'America/Indianapolis');
        if (force || !events || now < eventsAt || now - eventsAt > 60000 || window.MoonmeetEventTimeline.dateKey(now, zone) !== this.day) events = readTimeline(now, zone, force).upcoming;
        eventsAt = now;
        const key = events.map((event) => event.utc).join('|');
        if (this.eventKey === key && this.zone === zone && !force) return;
        this.querySelectorAll('[data-upcoming-event]').forEach((row, index) => {
          const event = events[index];
          setText(row.querySelector('[data-upcoming-name]'), this.dataset[event.phase]);
          setText(row.querySelector('[data-upcoming-icon]'), event.phase === 'new' ? '○' : '●');
          setText(row.querySelector('[data-upcoming-description]'), this.dataset[`${event.phase}Description`]);
          const time = row.querySelector('time'); time.dateTime = event.utc;
          setText(time, localTime(event.time).label);
          setLink(row.querySelector('[data-upcoming-link]'), event);
        });
        this.querySelector('[data-upcoming-values]').hidden = false;
        this.querySelector('[data-upcoming-fallback]').hidden = true;
        setText(this.querySelector('[data-upcoming-announcement]'), this.dataset.updated);
        this.dataset.state = 'ready'; this.eventKey = key; this.zone = zone; this.day = window.MoonmeetEventTimeline.dateKey(now, zone);
      } catch {
        events = undefined; this.eventKey = undefined;
        this.querySelector('[data-upcoming-values]').hidden = true;
        this.querySelector('[data-upcoming-fallback]').hidden = false;
        setText(this.querySelector('[data-upcoming-announcement]'), this.dataset.failure);
        this.dataset.state = 'fallback';
      }
    }
  }
  class MoonmeetLiveMoon extends HTMLElement {
    connectedCallback() { this.phases = JSON.parse(this.dataset.phases); register(this); }
    disconnectedCallback() { unregister(this); }
    refresh(force) {
      try {
        const reading = readMoon(Date.now(), force);
        if (this.observed === reading.time && !force) return;
        const disk = window.MoonmeetAstronomy.phaseDisk(reading.fraction, reading.waxing);
        const path = this.querySelector('[data-mini-lit]');
        path.setAttribute('d', disk.path); path.setAttribute('transform', disk.transform);
        setText(this.querySelector('[data-mini-phase]'), this.phases[reading.phaseIndex]);
        setText(this.querySelector('[data-mini-light]'), `${new Intl.NumberFormat(document.documentElement.lang || undefined, {style:'percent',maximumFractionDigits:1}).format(reading.fraction)} ${this.dataset.illuminated}`);
        const time = this.querySelector('time'); time.dateTime = new Date(reading.time).toISOString();
        setText(time, localTime(reading.time).label);
        this.querySelector('[data-mini-disk]').removeAttribute('hidden');
        this.querySelector('[data-mini-values]').hidden = false;
        this.querySelector('[data-mini-fallback]').hidden = true;
        this.dataset.state = 'ready'; this.observed = reading.time;
      } catch {
        this.querySelector('[data-mini-disk]').setAttribute('hidden', '');
        this.querySelector('[data-mini-values]').hidden = true;
        this.querySelector('[data-mini-fallback]').hidden = false;
        this.dataset.state = 'fallback'; this.observed = undefined;
      }
    }
  }
  class MoonmeetEventList extends HTMLElement {
    connectedCallback() { register(this); }
    disconnectedCallback() { unregister(this); }
    refresh(force) {
      try {
        const now = Date.now(), zone = window.MoonmeetEventTimeline.zoneOf(this.dataset.zone || 'America/Indianapolis');
        const state = readTimeline(now, zone, force);
        if (this.day === state.today && !force) return;
        const groups = { today: state.current ? [state.current] : [], upcoming: state.upcoming, recent: state.recent };
        for (const [name, items] of Object.entries(groups)) {
          const group = this.querySelector(`[data-list-${name}]`);
          group.hidden = !items.length;
          const list = group.querySelector('ol');
          list.replaceChildren(...items.map((event) => {
            const li = document.createElement('li'); li.className = 'mm-event-card';
            const icon = document.createElement('span'); icon.className = 'mm-event-card__icon'; icon.setAttribute('aria-hidden', 'true'); icon.textContent = event.phase === 'new' ? '○' : '●';
            const title = document.createElement('h3'); title.textContent = this.dataset[event.phase];
            const date = document.createElement('time'); date.dateTime = event.utc;
            date.textContent = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { dateStyle: 'full', timeZone: zone }).format(event.time);
            const detail = document.createElement('p'); detail.textContent = `${this.dataset.astronomical}: ${localTime(event.time).label}`;
            li.append(icon, title, date, detail);
            const record = recordFor(event), url = record?.dataset.url;
            if (url?.startsWith('/') && !url.startsWith('//')) {
              const link = document.createElement('a'); link.href = url; link.className = 'mm-text-link'; link.textContent = this.dataset.explore;
              li.append(link);
            } else {
              const note = document.createElement('p'); note.className = 'mm-event-card__pending'; note.textContent = this.dataset.pending; li.append(note);
            }
            return li;
          }));
        }
        this.querySelector('[data-list-fallback]').hidden = true;
        this.dataset.state = 'ready'; this.day = state.today;
      } catch {
        this.querySelectorAll('[data-list-today], [data-list-upcoming], [data-list-recent]').forEach((group) => { group.hidden = true; });
        this.querySelector('[data-list-fallback]').hidden = false;
        this.dataset.state = 'fallback'; this.day = undefined;
      }
    }
  }
  customElements.define('moonmeet-events', MoonmeetEvents);
  customElements.define('moonmeet-sky', MoonmeetSky);
  customElements.define('moonmeet-dashboard', MoonmeetDashboard);
  customElements.define('moonmeet-upcoming', MoonmeetUpcoming);
  customElements.define('moonmeet-live-moon', MoonmeetLiveMoon);
  customElements.define('moonmeet-event-list', MoonmeetEventList);
  // New lunar components share the existing heartbeat and live Moon snapshot.
  window.MoonmeetLunarUI = Object.freeze({ register, unregister, readMoon, readTimeline });
})();
